import { useEffect } from "react";
import { useClerk } from "@clerk/clerk-react";
import { isNative } from "@/lib/platform";

/**
 * Native only: catches the OAuth deep-link redirect
 * (fantasyfootballafrica://callback...) and completes the Clerk session
 * inside the WebView. Without this, native Google sign-in returns
 * "authorization_invalid" because the session never transfers back.
 */
export function useClerkNativeCallback() {
  const clerk = useClerk();

  useEffect(() => {
    if (!isNative) return;

    let listener: any;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isPluginAvailable("App")) return;
        const { App } = await import("@capacitor/app");
        const { Browser } = await import("@capacitor/browser");

        listener = await App.addListener("appUrlOpen", async ({ url }) => {
          // Only handle our OAuth callback deep link
          if (!url || !url.includes("com.fantasyfootballafrica.app://callback")) return;

          try {
            // Close the in-app browser that handled Google sign-in
            await Browser.close().catch(() => {});

            // Let Clerk finalize the session from the redirect URL.
            // handleRedirectCallback parses the rotating_token_nonce / params.
            await clerk.handleRedirectCallback(
              {
                afterSignInUrl: "/dashboard",
                afterSignUpUrl: "/onboarding/country-selection",
              },
              // navigate override — use hard location change inside WebView
              async (to: string) => {
                window.location.href = to;
              },
            );
          } catch (e) {
            console.error("Clerk native callback failed:", e);
            // Fallback: reload so Clerk picks up any established session
            try {
              await clerk.session?.reload?.();
            } catch {}
          }
        });
      } catch (e) {
        console.error("appUrlOpen listener setup failed:", e);
      }
    })();

    return () => {
      try {
        listener?.remove?.();
      } catch {}
    };
  }, [clerk]);
}
